/**
 * Dermotienda performance pass:
 * - coalesces theme-color style reads
 * - reuses geometry snapshots within one animation frame
 * - avoids redundant header CSS custom-property writes
 *
 * Request an idle callback or fallback to setTimeout
 * @returns {function} The requestIdleCallback function
 */
export const requestIdleCallback =
  typeof window.requestIdleCallback == 'function' ? window.requestIdleCallback : setTimeout;

/**
 * Executes a callback in a separate task after the next frame.
 * Using to defer non-critical tasks until after the interaction is complete.
 * @see https://web.dev/articles/optimize-inp#yield_to_allow_rendering-work-to-occur-sooner
 * @param {() => any} callback - The callback to execute
 */
export const requestYieldCallback = (callback) => {
  requestAnimationFrame(() => {
    setTimeout(callback, 0);
  });
};

/**
 * Tells if we are on a low power device based on the number of CPU cores and RAM
 * @returns {boolean} True if the device is a low power device, false otherwise
 */
export function isLowPowerDevice() {
  return Number(navigator.hardwareConcurrency) <= 2 || Number(navigator.deviceMemory) <= 2;
}

/**
 * Check if the browser supports View Transitions API
 * @returns {boolean} True if the browser supports View Transitions API, false otherwise
 */
export function supportsViewTransitions() {
  return typeof document.startViewTransition === 'function';
}

/**
 * The current view transition
 * @type {{ current: Promise<void> | undefined }}
 */
export const viewTransition = {
  current: undefined,
};

/**
 * Functions to run when a view transition of a given type is started
 * @type {{ [key: string]: () => Promise<(() => void) | undefined> }}
 */
const viewTransitionTypes = {
  'product-grid': async () => {
    const grid = document.querySelector('.product-grid');
    const productCards = /** @type {HTMLElement[]} */ ([
      ...document.querySelectorAll('.product-grid .product-grid__item'),
    ]);

    if (!grid || !productCards.length) return;

    await new Promise((resolve) =>
      requestIdleCallback(() => {
        const cardsToAnimate = getCardsToAnimate(grid, productCards);

        productCards.forEach((card, index) => {
          if (index < cardsToAnimate) {
            card.style.setProperty(
              'view-transition-name',
              `product-card-${card.dataset.productId}`
            );
          } else {
            card.style.setProperty('content-visibility', 'hidden');
          }
        });

        resolve(null);
      })
    );

    return () =>
      productCards.forEach((card) => {
        card.style.removeProperty('view-transition-name');
        card.style.removeProperty('content-visibility');
      });
  },
};

/**
 * Starts a view transition
 * @param {() => void} callback The callback to call when the view transition starts
 * @param {string[]} [types] The types of view transition to use
 * @returns {Promise<void>} A promise that resolves when the view transition finishes
 */
export function startViewTransition(callback, types) {
  // eslint-disable-next-line no-async-promise-executor
  return new Promise(async (resolve) => {
    if (supportsViewTransitions() && !prefersReducedMotion()) {
      let cleanupFunctions = [];

      if (types) {
        for (const type of types) {
          if (viewTransitionTypes[type]) {
            const cleanupFunction = await viewTransitionTypes[type]();

            if (cleanupFunction) {
              cleanupFunctions.push(cleanupFunction);
            }
          }
        }
      }

      const transition = document.startViewTransition(callback);

      if (!viewTransition.current) {
        viewTransition.current = transition.finished;
      }

      if (types) {
        types.forEach((type) => transition.types.add(type));
      }

      transition.finished.then(() => {
        viewTransition.current = undefined;

        cleanupFunctions.forEach((cleanupFunction) => {
          cleanupFunction();
        });

        resolve();
      });

      return;
    }

    callback();
    resolve();
  });
}

/**
 * @typedef {{ [key: string]: string | undefined }} Headers
 */

/**
 * @typedef {Object} FetchConfig
 * @property {string} method
 * @property {Headers} headers
 * @property {string | FormData | undefined} [body]
 */

/**
 * Creates a fetch configuration object
 * @param {string} [type] The type of response to expect
 * @param {Object} [config] The config of the request
 * @param {FetchConfig['body']} [config.body] The body of the request
 * @param {FetchConfig['headers']} [config.headers] The headers of the request
 * @returns {RequestInit} The fetch configuration object
 */
export function fetchConfig(type = 'json', config = {}) {
  /** @type {Headers} */
  const headers = {
    'Content-Type': 'application/json',
    Accept: `application/${type}`,
    ...config.headers,
  };

  if (type === 'javascript') {
    headers['X-Requested-With'] = 'XMLHttpRequest';
    delete headers['Content-Type'];
  }

  return {
    method: 'POST',
    headers: /** @type {HeadersInit} */ (headers),
    body: config.body,
  };
}

/**
 * Creates a debounced function that delays calling the provided function (fn)
 * until after wait milliseconds have elapsed since the last time
 * the debounced function was invoked. The returned function has a .cancel()
 * method to cancel any pending calls.
 *
 * @template {(...args: any[]) => any} T
 * @param {T} fn The function to debounce
 * @param {number} wait The time in milliseconds
 * @returns {T & { cancel(): void }}
 */
export function debounce(fn, wait) {
  /** @type {number | undefined} */
  let timeout;

  /** @param {...any} args */
  function debounced(...args) {
    clearTimeout(timeout);

    timeout = setTimeout(() => {
      fn.apply(this, args);
    }, wait);
  }

  debounced.cancel = () => {
    clearTimeout(timeout);
  };

  return /** @type {T & { cancel(): void }} */ (debounced);
}

/**
 * Creates a throttled function.
 *
 * @template {(...args: any[]) => any} T
 * @param {T} fn
 * @param {number} delay
 * @returns {T & { cancel(): void }}
 */
export function throttle(fn, delay) {
  let lastCall = 0;

  /** @param {...any} args */
  function throttled(...args) {
    const now = performance.now();

    if (now - lastCall >= delay) {
      lastCall = now;
      fn.apply(this, args);
    }
  }

  throttled.cancel = () => {
    lastCall = performance.now();
  };

  return /** @type {T & { cancel(): void }} */ (throttled);
}

/**
 * A media query for reduced motion
 * @type {MediaQueryList}
 */
const reducedMotion = matchMedia('(prefers-reduced-motion: reduce)');

/**
 * @returns {boolean}
 */
export function prefersReducedMotion() {
  return reducedMotion.matches;
}

/**
 * Normalize a string
 * @param {string} str
 * @returns {string}
 */
export function normalizeString(str) {
  return str
    .normalize('NFD')
    .replace(/\p{Diacritic}/gu, '')
    .toLowerCase();
}

/**
 * Format a money value
 * @param {string} value
 * @returns {string}
 */
export function formatMoney(value) {
  let valueWithNoSpaces = value.replace(' ', '');

  if (valueWithNoSpaces.indexOf(',') === -1) {
    return valueWithNoSpaces;
  }

  if (
    valueWithNoSpaces.indexOf(',') <
    valueWithNoSpaces.indexOf('.')
  ) {
    return valueWithNoSpaces.replace(',', '');
  }

  if (
    valueWithNoSpaces.indexOf('.') <
    valueWithNoSpaces.indexOf(',')
  ) {
    return valueWithNoSpaces
      .replace('.', '')
      .replace(',', '.');
  }

  if (valueWithNoSpaces.indexOf(',') !== -1) {
    return valueWithNoSpaces.replace(',', '.');
  }

  return valueWithNoSpaces;
}

/**
 * @param {() => void} callback
 */
export function onDocumentLoaded(callback) {
  if (document.readyState === 'complete') {
    callback();
  } else {
    window.addEventListener('load', callback, {
      once: true,
    });
  }
}

/**
 * @param {() => void} callback
 */
export function onDocumentReady(callback) {
  if (document.readyState === 'loading') {
    document.addEventListener(
      'DOMContentLoaded',
      callback,
      {
        once: true,
      }
    );
  } else {
    callback();
  }
}

/**
 * Wait for all animations to finish before calling the callback.
 * @param {Element | Element[]} elements
 * @param {() => void} [callback]
 * @param {Object} [options]
 * @returns {Promise<void>}
 */
export function onAnimationEnd(
  elements,
  callback,
  options = { subtree: true }
) {
  const animations = Array.isArray(elements)
    ? elements.flatMap((element) =>
        element.getAnimations(options)
      )
    : elements.getAnimations(options);

  const animationPromises = animations.reduce(
    (acc, animation) => {
      if (
        animation.timeline instanceof DocumentTimeline
      ) {
        acc.push(animation.finished);
      }

      return acc;
    },
    /** @type {Promise<Animation>[]} */ ([])
  );

  return Promise.allSettled(
    animationPromises
  ).then(callback);
}

/**
 * Check if click is outside element.
 * @param {MouseEvent} event
 * @param {Element} element
 * @returns {boolean}
 */
export function isClickedOutside(event, element) {
  if (
    event.target instanceof HTMLDialogElement ||
    !(event.target instanceof Element)
  ) {
    return !isPointWithinElement(
      event.clientX,
      event.clientY,
      element
    );
  }

  return !element.contains(event.target);
}

/**
 * @param {number} x
 * @param {number} y
 * @param {Element} element
 * @returns {boolean}
 */
export function isPointWithinElement(
  x,
  y,
  element
) {
  const {
    left,
    right,
    top,
    bottom,
  } = element.getBoundingClientRect();

  return (
    x >= left &&
    x <= right &&
    y >= top &&
    y <= bottom
  );
}

/**
 * Large breakpoint
 * @type {MediaQueryList}
 */
export const mediaQueryLarge = matchMedia(
  '(min-width: 750px)'
);

/**
 * @returns {boolean}
 */
export function isMobileBreakpoint() {
  return !mediaQueryLarge.matches;
}

/**
 * @returns {boolean}
 */
export function isDesktopBreakpoint() {
  return mediaQueryLarge.matches;
}

/**
 * @param {number} value
 * @param {number} min
 * @param {number} max
 * @returns {number}
 */
export function clamp(value, min, max) {
  return Math.max(
    min,
    Math.min(value, max)
  );
}

/**
 * Calculates the center point.
 *
 * @template {('x' | 'y')} T
 * @param {Element} element
 * @param {T} [axis]
 * @returns {T extends ('x' | 'y') ? number : {x: number, y: number}}
 */
export function center(element, axis) {
  const {
    left,
    width,
    top,
    height,
  } = element.getBoundingClientRect();

  const point = {
    x: left + width / 2,
    y: top + height / 2,
  };

  if (axis) {
    return /** @type {any} */ (point[axis]);
  }

  return /** @type {any} */ (point);
}

/**
 * Calculates start point.
 * @param {Element} element
 * @param {'x' | 'y'} [axis]
 * @returns {number | {x: number, y: number}}
 */
export function start(element, axis) {
  const {
    left,
    top,
  } = element.getBoundingClientRect();

  const point = {
    x: left,
    y: top,
  };

  if (axis) {
    return /** @type {any} */ (point[axis]);
  }

  return /** @type {any} */ (point);
}

/**
 * Finds the closest value.
 * @param {number[]} values
 * @param {number} target
 * @returns {number}
 */
export function closest(values, target) {
  return values.reduce(function (
    prev,
    curr
  ) {
    return Math.abs(curr - target) <
      Math.abs(prev - target)
      ? curr
      : prev;
  });
}

/**
 * @param {Event} event
 */
export function preventDefault(event) {
  event.preventDefault();
}

/*
 * ============================================================
 * GEOMETRY CACHE
 * ============================================================
 *
 * getBoundingClientRect() may force style/layout when other
 * scripts modified DOM/CSS immediately before the read.
 *
 * Several theme components request the same geometry during
 * a single frame. We therefore reuse the measurement until
 * the next requestAnimationFrame.
 * ============================================================
 */

let geometryRectCache = new WeakMap();
let geometryCacheResetScheduled = false;

/**
 * @param {Element} element
 * @returns {DOMRect}
 */
function getFrameRect(element) {
  const cached =
    geometryRectCache.get(element);

  if (cached) {
    return cached;
  }

  const rect =
    element.getBoundingClientRect();

  geometryRectCache.set(
    element,
    rect
  );

  if (!geometryCacheResetScheduled) {
    geometryCacheResetScheduled = true;

    requestAnimationFrame(() => {
      geometryRectCache =
        new WeakMap();

      geometryCacheResetScheduled =
        false;
    });
  }

  return rect;
}

/**
 * Get visible elements within root.
 *
 * @template {Element} T
 * @param {Element} root
 * @param {T[] | undefined} elements
 * @param {number} [ratio=1]
 * @param {'x' | 'y'} [axis]
 * @returns {T[]}
 */
export function getVisibleElements(
  root,
  elements,
  ratio = 1,
  axis
) {
  if (!elements?.length) {
    return [];
  }

  const rootRect =
    getFrameRect(root);

  const visible = [];

  const checkX = axis !== 'y';
  const checkY = axis !== 'x';

  const partialVisibility =
    ratio < 1;

  for (const element of elements) {
    const rect =
      getFrameRect(element);

    const {
      width,
      height,
      top,
      right,
      left,
      bottom,
    } = rect;

    if (partialVisibility) {
      let xRatio = 1;
      let yRatio = 1;

      if (checkX) {
        if (width <= 0) {
          continue;
        }

        const intersectionLeft =
          Math.max(
            rootRect.left,
            left
          );

        const intersectionRight =
          Math.min(
            rootRect.right,
            right
          );

        const intersectionWidth =
          Math.max(
            0,
            intersectionRight -
              intersectionLeft
          );

        xRatio =
          intersectionWidth / width;

        if (axis === 'x') {
          if (xRatio >= ratio) {
            visible.push(element);
          }

          continue;
        }

        if (
          xRatio < ratio &&
          axis === undefined
        ) {
          continue;
        }
      }

      if (checkY) {
        if (height <= 0) {
          continue;
        }

        const intersectionTop =
          Math.max(
            rootRect.top,
            top
          );

        const intersectionBottom =
          Math.min(
            rootRect.bottom,
            bottom
          );

        const intersectionHeight =
          Math.max(
            0,
            intersectionBottom -
              intersectionTop
          );

        yRatio =
          intersectionHeight / height;

        if (axis === 'y') {
          if (yRatio >= ratio) {
            visible.push(element);
          }

          continue;
        }
      }

      /*
       * Preserve original behavior:
       * when no axis is provided,
       * calculate intersection area.
       */
      if (axis === undefined) {
        const elementArea =
          width * height;

        if (elementArea <= 0) {
          continue;
        }

        const intersectionLeft =
          Math.max(
            rootRect.left,
            left
          );

        const intersectionRight =
          Math.min(
            rootRect.right,
            right
          );

        const intersectionTop =
          Math.max(
            rootRect.top,
            top
          );

        const intersectionBottom =
          Math.min(
            rootRect.bottom,
            bottom
          );

        const intersectionWidth =
          Math.max(
            0,
            intersectionRight -
              intersectionLeft
          );

        const intersectionHeight =
          Math.max(
            0,
            intersectionBottom -
              intersectionTop
          );

        const intersectionArea =
          intersectionWidth *
          intersectionHeight;

        if (
          intersectionArea /
            elementArea >=
          ratio
        ) {
          visible.push(element);
        }
      }

      continue;
    }

    const isWithinX =
      !checkX ||
      (
        left >= rootRect.left &&
        right <= rootRect.right
      );

    const isWithinY =
      !checkY ||
      (
        top >= rootRect.top &&
        bottom <= rootRect.bottom
      );

    if (
      isWithinX &&
      isWithinY
    ) {
      visible.push(element);
    }
  }

  return visible;
}

/**
 * Gets iOS version.
 */
export function getIOSVersion() {
  const { userAgent } = navigator;

  const isIOS =
    /(iPhone|iPad)/i.test(
      userAgent
    );

  if (!isIOS) {
    return null;
  }

  const version =
    userAgent.match(
      /OS ([\d_]+)/
    )?.[1];

  const [major, minor] =
    version?.split('_') || [];

  if (
    !version ||
    !major
  ) {
    return null;
  }

  return {
    fullString:
      version.replace('_', '.'),
    major:
      parseInt(major, 10),
    minor:
      minor
        ? parseInt(minor, 10)
        : 0,
  };
}

/**
 * Determines grid items to animate.
 *
 * @param {Element} grid
 * @param {Element[]} cards
 * @returns {number}
 */
function getCardsToAnimate(
  grid,
  cards
) {
  if (
    !grid ||
    !cards ||
    cards.length === 0
  ) {
    return 0;
  }

  const itemSample = cards[0];

  if (!itemSample) {
    return 0;
  }

  const gridRect =
    grid.getBoundingClientRect();

  const visibleArea = {
    top: Math.max(
      0,
      gridRect.top
    ),
    bottom: Math.min(
      window.innerHeight,
      gridRect.bottom
    ),
  };

  const visibleHeight =
    Math.round(
      visibleArea.bottom -
        visibleArea.top
    );

  if (visibleHeight <= 0) {
    return 0;
  }

  /** @type {import('product-card').ProductCard | null} */
  const cardSample =
    itemSample.querySelector(
      'product-card'
    );

  const gridStyle =
    getComputedStyle(grid);

  const galleryAspectRatio =
    cardSample?.refs?.cardGallery?.style.getPropertyValue(
      '--gallery-aspect-ratio'
    ) || '';

  let aspectRatio =
    parseFloat(
      galleryAspectRatio
    ) || 0.5;

  if (
    galleryAspectRatio?.includes('/')
  ) {
    const [
      width = '1',
      height = '2',
    ] =
      galleryAspectRatio.split('/');

    aspectRatio =
      parseInt(width, 10) /
      parseInt(height, 10);
  }

  const cardGap =
    parseInt(
      cardSample?.refs?.productCardLink?.style.getPropertyValue(
        '--product-card-gap'
      ) || ''
    ) || 12;

  const gridGap =
    parseInt(
      gridStyle.getPropertyValue(
        '--product-grid-gap'
      )
    ) || 12;

  const detailsSize =
    (
      (
        parseInt(
          gridStyle.fontSize
        ) || 16
      ) +
      2
    ) * 2;

  const isMobile =
    window.innerWidth < 750;

  const cardWidth =
    isMobile
      ? Math.round(
          (
            gridRect.width -
            gridGap
          ) / 2
        )
      : 100;

  const cardHeight =
    Math.round(
      cardWidth / aspectRatio
    ) +
    cardGap +
    detailsSize;

  const columnsInGrid =
    isMobile
      ? 2
      : Math.floor(
          (
            gridRect.width +
            gridGap
          ) /
            (
              cardWidth +
              gridGap
            )
        );

  const rowsInGrid =
    Math.ceil(
      (
        visibleHeight -
        gridGap
      ) /
        (
          cardHeight +
          gridGap
        )
    );

  return (
    columnsInGrid *
    rowsInGrid
  );
}

/**
 * Preloads an image.
 * @param {string} src
 */
export function preloadImage(src) {
  const image = new Image();

  image.src = src;
}

export class TextComponent extends HTMLElement {
  shimmer() {
    this.setAttribute(
      'shimmer',
      ''
    );
  }
}

if (
  !customElements.get(
    'text-component'
  )
) {
  customElements.define(
    'text-component',
    TextComponent
  );
}

/**
 * Resets shimmer.
 * @param {Element} [container]
 */
export function resetShimmer(
  container = document.body
) {
  const shimmer =
    container.querySelectorAll(
      '[shimmer]'
    );

  shimmer.forEach((item) => {
    item.removeAttribute(
      'shimmer'
    );
  });
}

/*
 * ============================================================
 * META THEME COLOR OPTIMIZATION
 * ============================================================
 */

let pendingThemeColorSource = null;

let themeColorFrame = 0;

let cachedMetaThemeColor = null;

let lastThemeColor = '';

/**
 * Change the meta theme color of the header.
 *
 * Multiple requests during the same frame are collapsed
 * into one getComputedStyle() call.
 *
 * @param {Element} colorSourceElement
 */
export function changeMetaThemeColor(
  colorSourceElement
) {
  if (
    !(
      colorSourceElement instanceof
      Element
    )
  ) {
    return;
  }

  pendingThemeColorSource =
    colorSourceElement;

  if (themeColorFrame) {
    return;
  }

  themeColorFrame =
    requestAnimationFrame(() => {
      themeColorFrame = 0;

      const source =
        pendingThemeColorSource;

      pendingThemeColorSource =
        null;

      if (
        !(
          source instanceof Element
        ) ||
        !source.isConnected
      ) {
        return;
      }

      cachedMetaThemeColor ||=
        document.head.querySelector(
          'meta[name="theme-color"]'
        );

      if (!cachedMetaThemeColor) {
        return;
      }

      /*
       * Prefer inline background when possible.
       * Reading style.backgroundColor does not
       * trigger style recalculation.
       */
      const inlineColor =
        source instanceof HTMLElement
          ? source.style.backgroundColor
          : '';

      const backgroundColor =
        inlineColor ||
        window.getComputedStyle(
          source
        ).backgroundColor;

      if (
        !backgroundColor ||
        backgroundColor ===
          lastThemeColor
      ) {
        return;
      }

      lastThemeColor =
        backgroundColor;

      cachedMetaThemeColor.setAttribute(
        'content',
        backgroundColor
      );
    });
}

/**
 * Gets view URL parameter.
 * @returns {string | null}
 */
export function getViewParameterValue() {
  return new URLSearchParams(
    window.location.search
  ).get('view');
}

/**
 * Helper to parse integer.
 *
 * @template {number|null} T
 * @param {string|number|null|undefined} value
 * @param {T} defaultValue
 * @returns {number|T}
 */
export function parseIntOrDefault(
  value,
  defaultValue
) {
  if (
    value === null ||
    value === undefined ||
    value === ''
  ) {
    return defaultValue;
  }

  const parsed =
    parseInt(
      value.toString()
    );

  return isNaN(parsed)
    ? defaultValue
    : parsed;
}

/*
 * ============================================================
 * SCHEDULER
 * ============================================================
 */

class Scheduler {
  /** @type {Set<() => void>} */
  #queue = new Set();

  /** @type {boolean} */
  #scheduled = false;

  /**
   * @param {() => void} task
   */
  schedule = async (task) => {
    this.#queue.add(task);

    if (!this.#scheduled) {
      this.#scheduled = true;

      if (
        viewTransition.current
      ) {
        await viewTransition.current;
      }

      requestAnimationFrame(
        this.flush
      );
    }
  };

  flush = () => {
    for (
      const task of this.#queue
    ) {
      setTimeout(
        task,
        0
      );
    }

    this.#queue.clear();

    this.#scheduled = false;
  };
}

export const scheduler =
  new Scheduler();

/**
 * Executes callback once per editor session.
 *
 * @param {HTMLElement} element
 * @param {string} sessionKeyName
 * @param {() => void} callback
 */
export function oncePerEditorSession(
  element,
  sessionKeyName,
  callback
) {
  const isInThemeEditor =
    window.Shopify?.designMode;

  const shopifyEditorSectionId =
    JSON.parse(
      element.dataset
        .shopifyEditorSection ||
        '{}'
    ).id;

  const shopifyEditorBlockId =
    JSON.parse(
      element.dataset
        .shopifyEditorBlock ||
        '{}'
    ).id;

  const editorId =
    shopifyEditorSectionId ||
    shopifyEditorBlockId;

  const uniqueSessionKey =
    `${sessionKeyName}-${editorId}`;

  if (
    isInThemeEditor &&
    sessionStorage.getItem(
      uniqueSessionKey
    )
  ) {
    return;
  }

  callback();

  if (isInThemeEditor) {
    sessionStorage.setItem(
      uniqueSessionKey,
      'true'
    );
  }

  return;
}

/**
 * Custom ResizeObserver.
 */
export class ResizeNotifier extends ResizeObserver {
  #initialized = false;

  /**
   * @param {ResizeObserverCallback} callback
   */
  constructor(callback) {
    super((entries) => {
      if (this.#initialized) {
        return callback(
          entries,
          this
        );
      }

      this.#initialized = true;
    });
  }

  disconnect() {
    this.#initialized = false;

    super.disconnect();
  }
}

/*
 * ============================================================
 * HEADER CALCULATIONS
 * ============================================================
 */

/**
 * @param {Element | null} header
 * @param {Element | null} headerGroup
 * @returns {number}
 */
export function calculateHeaderGroupHeight(
  header =
    document.querySelector(
      '#header-component'
    ),
  headerGroup =
    document.querySelector(
      '#header-group'
    )
) {
  if (!headerGroup) {
    return 0;
  }

  let totalHeight = 0;

  const children =
    headerGroup.children;

  for (
    let i = 0;
    i < children.length;
    i++
  ) {
    const element =
      children[i];

    if (
      element === header ||
      !(
        element instanceof
        HTMLElement
      )
    ) {
      continue;
    }

    totalHeight +=
      element.offsetHeight;
  }

  if (
    header instanceof
      HTMLElement &&
    header.hasAttribute(
      'transparent'
    ) &&
    header.parentElement
      ?.nextElementSibling
  ) {
    return (
      totalHeight +
      header.offsetHeight
    );
  }

  return totalHeight;
}

/**
 * Updates transparent header offset.
 *
 * Avoids writing the CSS variable again
 * when the value did not change.
 */
function updateTransparentHeaderOffset() {
  const header =
    document.querySelector(
      '#header-component'
    );

  const headerGroup =
    document.querySelector(
      '#header-group'
    );

  const hasHeaderSection =
    headerGroup?.querySelector(
      '.header-section'
    );

  let shouldApplyOffset = '0';

  if (
    hasHeaderSection &&
    header?.hasAttribute(
      'transparent'
    )
  ) {
    const hasImmediateSection =
      hasHeaderSection
        .nextElementSibling
        ?.classList.contains(
          'shopify-section'
        );

    shouldApplyOffset =
      !hasImmediateSection
        ? '1'
        : '0';
  }

  const currentValue =
    document.body.style.getPropertyValue(
      '--transparent-header-offset-boolean'
    );

  if (
    currentValue !==
    shouldApplyOffset
  ) {
    document.body.style.setProperty(
      '--transparent-header-offset-boolean',
      shouldApplyOffset
    );
  }
}

/**
 * Updates header dimensions.
 *
 * Reads dimensions first.
 * Writes CSS variables only when necessary.
 */
function updateHeaderHeights() {
  const header =
    document.querySelector(
      'header-component'
    );

  if (
    !(
      header instanceof
      HTMLElement
    )
  ) {
    return;
  }

  /*
   * READ PHASE
   */
  const headerHeightValue =
    `${header.offsetHeight}px`;

  const headerGroupHeightValue =
    `${calculateHeaderGroupHeight(
      header
    )}px`;

  /*
   * WRITE PHASE
   */
  const bodyStyle =
    document.body.style;

  if (
    bodyStyle.getPropertyValue(
      '--header-height'
    ) !== headerHeightValue
  ) {
    bodyStyle.setProperty(
      '--header-height',
      headerHeightValue
    );
  }

  if (
    bodyStyle.getPropertyValue(
      '--header-group-height'
    ) !==
    headerGroupHeightValue
  ) {
    bodyStyle.setProperty(
      '--header-group-height',
      headerGroupHeightValue
    );
  }
}

/**
 * Updates all header custom properties.
 */
export function updateAllHeaderCustomProperties() {
  updateHeaderHeights();

  updateTransparentHeaderOffset();
}

/*
 * Preserve public Theme API.
 */
Theme.utilities = {
  ...Theme.utilities,
  scheduler: scheduler,
};